/**
 * PMR171 参数同步 0x2E（DEVICE_POWER_SHUTDOWN_API.md §6）
 * MQTT device/{userId}/{deviceId}/status，type === "0X2E"，data.params 为短键名快照。
 */

/** 与文档 data.params 键名一致（0～255 无符号，前端按字段再 clip 到 AF/RF 控件范围） */
export interface Pmr171ParamSyncParams {
  svol: number;
  hvol: number;
  mic: number;
  cmp: number;
  bas: number;
  trb: number;
  rfg: number;
  ifg: number;
  sql: number;
  agc: number;
  amp: number;
  nr: number;
  nb: number;
  peak: number;
  span: number;
  ref: number;
  speed: number;
  tCtss: number;
  rCtss: number;
  lVoice: number;
  lTime: number;
  keyMode: number;
  txRx: number;
  traning: number;
  stf: number;
  stg: number;
  keySpeed: number;
  decode: number;
  threshold: number;
  dataFormat: number;
}

const PARAM_KEYS = [
  'svol',
  'hvol',
  'mic',
  'cmp',
  'bas',
  'trb',
  'rfg',
  'ifg',
  'sql',
  'agc',
  'amp',
  'nr',
  'nb',
  'peak',
  'span',
  'ref',
  'speed',
  'tCtss',
  'rCtss',
  'lVoice',
  'lTime',
  'keyMode',
  'txRx',
  'traning',
  'stf',
  'stg',
  'keySpeed',
  'decode',
  'threshold',
  'dataFormat',
] as const;

export function createDefaultPmr171ParamSyncParams(): Pmr171ParamSyncParams {
  const o = {} as Pmr171ParamSyncParams;
  for (const k of PARAM_KEYS) {
    (o as unknown as Record<string, number>)[k] = 0;
  }
  return o;
}

function clampU8(n: number): number {
  const r = Math.round(n);
  if (!Number.isFinite(r)) return 0;
  return Math.min(255, Math.max(0, r));
}

/** 从 MQTT 原始 params 对象提取部分字段 */
export function parsePartialParamsFrom0x2eRaw(raw: unknown): Partial<Pmr171ParamSyncParams> {
  if (raw == null || typeof raw !== 'object') return {};
  const o = raw as Record<string, unknown>;
  const out: Partial<Pmr171ParamSyncParams> = {};
  for (const k of PARAM_KEYS) {
    if (k in o && typeof o[k] === 'number' && Number.isFinite(o[k])) {
      (out as Record<string, number>)[k] = clampU8(o[k] as number);
    }
  }
  return out;
}

export function mergePmr171ParamSync(
  prev: Pmr171ParamSyncParams | undefined,
  incoming: Partial<Pmr171ParamSyncParams>
): Pmr171ParamSyncParams {
  return { ...(prev ?? createDefaultPmr171ParamSyncParams()), ...incoming };
}

/** AudioManager 设备状态 listener 用：解析 0X2E 包 */
export function tryParsePmr171ParamSync0x2eFromStatus(
  statusObj: unknown,
  frequencyStatus: unknown,
  topicDeviceId: string
): { deviceId: string; timestamp: number; params: Partial<Pmr171ParamSyncParams> } | null {
  if (frequencyStatus == null || typeof frequencyStatus !== 'object') return null;
  const fs = frequencyStatus as Record<string, unknown>;
  const paramsRaw = fs.params;
  const params = parsePartialParamsFrom0x2eRaw(paramsRaw);
  if (Object.keys(params).length === 0) return null;

  let deviceId = topicDeviceId;
  if (statusObj != null && typeof statusObj === 'object' && 'deviceId' in statusObj) {
    const d = (statusObj as { deviceId?: unknown }).deviceId;
    if (d != null && String(d).trim() !== '') deviceId = String(d);
  }

  let timestamp = Date.now();
  if (statusObj != null && typeof statusObj === 'object' && 'timestamp' in statusObj) {
    const t = (statusObj as { timestamp?: unknown }).timestamp;
    if (typeof t === 'number' && Number.isFinite(t)) timestamp = t;
  }

  return { deviceId, timestamp, params };
}

function clampToRange(n: number, min: number, max: number): number {
  const r = Math.round(n);
  if (!Number.isFinite(r)) return min;
  return Math.min(max, Math.max(min, r));
}

/** AF Slider id ← 0x2E params（与 PMR171_AF_VOLUME_SLIDERS.id 对应） */
export function buildAfSliderValuesFromParamSync(
  params: Pmr171ParamSyncParams | undefined,
  specs: ReadonlyArray<{ id: string; min: number; max: number; defaultValue: number }>
): Record<string, number> {
  const p = params ?? createDefaultPmr171ParamSyncParams();
  const out: Record<string, number> = {};
  for (const s of specs) {
    let raw: number;
    switch (s.id) {
      case 'speaker':
        raw = p.svol;
        break;
      case 'headphone':
        raw = p.hvol;
        break;
      case 'mic':
        raw = p.mic;
        break;
      case 'compander':
        raw = p.cmp;
        break;
      case 'bass':
        raw = p.bas;
        break;
      case 'treble':
        raw = p.trb;
        break;
      default:
        raw = s.defaultValue;
    }
    out[s.id] = clampToRange(raw, s.min, s.max);
  }
  return out;
}

/** RF Slider id ← 0x2E params（amp → preamp） */
export function buildRfSliderValuesFromParamSync(
  params: Pmr171ParamSyncParams | undefined,
  specs: ReadonlyArray<{ id: string; min: number; max: number; defaultValue: number }>
): Record<string, number> {
  const p = params ?? createDefaultPmr171ParamSyncParams();
  const out: Record<string, number> = {};
  for (const s of specs) {
    let raw: number;
    switch (s.id) {
      case 'rfGain':
        raw = p.rfg;
        break;
      case 'ifGain':
        raw = p.ifg;
        break;
      case 'squelch':
        raw = p.sql;
        break;
      case 'agc':
        raw = p.agc;
        break;
      case 'preamp':
        raw = p.amp;
        break;
      default:
        raw = s.defaultValue;
    }
    out[s.id] = clampToRange(raw, s.min, s.max);
  }
  return out;
}

/** 用户通过 AF MQTT 修改后写回同一快照对象 */
export function patchParamSyncFromAfDataKey(
  dataKey:
    | 'speakerVolume'
    | 'headphoneVolume'
    | 'micGain'
    | 'companderRatio'
    | 'bassEq'
    | 'trebleEq',
  value: number
): Partial<Pmr171ParamSyncParams> {
  const v = Math.round(value);
  switch (dataKey) {
    case 'speakerVolume':
      return { svol: clampU8(v) };
    case 'headphoneVolume':
      return { hvol: clampU8(v) };
    case 'micGain':
      return { mic: clampU8(v) };
    case 'companderRatio':
      return { cmp: clampU8(v) };
    case 'bassEq':
      return { bas: clampU8(v) };
    case 'trebleEq':
      return { trb: clampU8(v) };
    default:
      return {};
  }
}

/** 用户通过 RF MQTT 修改后写回同一快照 */
export function patchParamSyncFromRfDataKey(
  dataKey: 'rfGain' | 'ifGain' | 'squelch' | 'agc' | 'preamp',
  value: number
): Partial<Pmr171ParamSyncParams> {
  const v = Math.round(value);
  switch (dataKey) {
    case 'rfGain':
      return { rfg: clampU8(v) };
    case 'ifGain':
      return { ifg: clampU8(v) };
    case 'squelch':
      return { sql: clampU8(v) };
    case 'agc':
      return { agc: clampU8(v) };
    case 'preamp':
      return { amp: clampU8(v) };
    default:
      return {};
  }
}
